import React, { useEffect, useState } from "react";
import { Container, Form, Card } from "react-bootstrap";
import { Formik, FieldArray } from "formik";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { useToast } from "../../../context/TostContext";
import { useLoader } from "../../../context/LoaderContext";
import CustomSelect from "../../../components/form/CustomSelect";
import Input from "../../../components/form";
import CommonButton from "../../../components/commonButton";
import { PlusIcon, Cancel01Icon } from "../../../assets/icons";

import {
  fetchFeatures,
  fetchPlanById,
  createPlan,
  updatePlan,
} from "../../../redux/slice/subscriptionSlice";
import InputField from "../../../components/form/InputField";
import { FormLabel } from "../../../components/form/utils";

const validationSchema = Yup.object().shape({
  name: Yup.string().required("Plan name is required"),
  // description: Yup.string().required("Description is required"),
  plan_type: Yup.string().required("Plan type is required"),
  price: Yup.number().when("plan_type", {
    is: "free",
    then: (schema) => schema.required("Price is required").oneOf([0], "Free plan must have 0 price"),
    otherwise: (schema) => schema.required("Price is required").min(0.01, "Paid plan must have price > 0"),
  }),
  currency: Yup.string().required("Currency is required"),
  interval: Yup.string().required("Interval is required"),
  interval_count: Yup.number().required("Count is required").min(1, "Must be at least 1"),
  trial_days: Yup.number().when("is_trial_available", {
    is: true,
    then: (schema) => schema.required("Trial days required").min(1, "Min 1 day"),
    otherwise: (schema) => schema.notRequired(),
  }),
  features: Yup.array().of(
    Yup.object().shape({
      feature_id: Yup.string().uuid().required("Feature selection is required"),
      value: Yup.mixed().required("Value is required"),
    })
  ).optional(),
});

const renderFeatureValueInput = (type, value, onChange, isEdit = false) => {
  if (type === "boolean") {
    return (
      <Form.Check
        type="switch"
        checked={Boolean(value)}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-2 shadow-none custom-plan-switch"
      />
    );
  }
  if (type === "number") {
    return (
      <Input
        type="number"
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        placeholder="Enter number"
        className="shadow-none"
      />
    );
  }
  return (
    <Input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder="Enter value"
      className="shadow-none"
    />
  );
};

const CreateEditPlan = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const toast = useToast();
  const { setLoading } = useLoader();

  const isEditMode = Boolean(id);
  const [selectedItem, setSelectedItem] = useState(null);

  const { features = [], isLoading } = useSelector((state) => state.subscription || {});

  useEffect(() => {
    // Ensure features are loaded for the dropdowns
    dispatch(fetchFeatures({ limit: 1000 }));
  }, [dispatch]);

  useEffect(() => {
    if (isEditMode) {
      setLoading(true);
      dispatch(fetchPlanById({ id, include_features: true }))
        .unwrap()
        .then((res) => {
          if (res?.data) {
            setSelectedItem(res.data);
          }
        })
        .catch((err) => {
          toast.error(err?.message || "Failed to fetch plan details");
          navigate("/subscriptions/plans");
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [dispatch, id, isEditMode, navigate, setLoading, toast]);

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      let result;
      if (isEditMode) {
        result = await dispatch(updatePlan({ id, data: values })).unwrap();
      } else {
        result = await dispatch(createPlan(values)).unwrap();
      }

      if (result?.success) {
        toast.success(result.message || "Plan saved successfully!");
        navigate("/subscriptions/plans");
      }
    } catch (err) {
      toast.error(err?.message || "Operation failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (isEditMode && !selectedItem && isLoading) {
    return null; // Loader context will handle showing spinner
  }

  return (
    <Container fluid className="px-0">
      <Card className="border-0 shadow-sm mt-5 ">
        <Card.Header className="d-flex align-items-center bg-white border-bottom-0 px-4 pt-4 pb-0">
          <Card.Title className="flex-grow-1 m-0">{isEditMode ? "Edit Plan" : "Add Plan"}</Card.Title>
          <div
              className="cursor-pointer d-flex align-items-center justify-content-center p-2 rounded-circle bg-light transition-all hover-shadow"
              onClick={() => navigate("/subscriptions/plans")}
            >
              <Cancel01Icon size={20} />
            </div>
        </Card.Header>



        {(selectedItem || !isEditMode) && (
          <Formik
            initialValues={{
              name: selectedItem?.name || "",
              description: selectedItem?.description || "",
              plan_type: selectedItem?.plan_type || "paid",
              price: selectedItem?.price || 0,
              currency: selectedItem?.currency || "INR",
              interval: selectedItem?.interval || "monthly",
              interval_count: selectedItem?.interval_count || 1,
              is_trial_available: selectedItem?.is_trial_available || false,
              trial_days: selectedItem?.trial_days || 0,
              notes: selectedItem?.notes || "",
              features:
                selectedItem?.features?.map((f) => ({
                  feature_id: f.id,
                  value: f.PlanFeature?.value ?? f.value,
                })) || [],
            }}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
            enableReinitialize
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleSubmit,
              isSubmitting,
              setFieldValue,
            }) => (
              <Form onSubmit={handleSubmit}>
                <Card.Body className="p-4 grid gap-3">
                  <InputField
                    className="g-col-6"
                    label={{ name: "Plan Name" }}
                    input={{
                      id: "name",
                      name: "name",
                      placeholder: "Enter plan name",
                      value: values.name,
                      onChange: handleChange,
                    }}
                    validation={{
                      isInvalid: touched.name && errors.name,
                      message: errors.name,
                    }}
                  />

                  <CustomSelect
                    label={{ name: "Plan Type" }}
                    name="plan_type"
                    options={[
                      { value: "paid", label: "Paid" },
                      { value: "free", label: "Free" },
                    ]}
                    value={values.plan_type}
                    onChange={(opt) => {
                      setFieldValue("plan_type", opt ? opt.value : "");
                      if (opt?.value === "free") {
                        setFieldValue("price", 0);
                      }
                    }}
                    className="g-col-6"
                  />
                  <InputField
                    className="g-col-6"
                    label={{ name: "Price" }}
                    input={{
                      id: "price",
                      name: "price",
                      type: "number",
                      min: 0,
                      placeholder: "Enter price",
                      value: values.price,
                      onChange: handleChange,
                      disabled: values.plan_type === "free",
                    }}
                    validation={{
                      isInvalid: touched.price && errors.price,
                      message: errors.price,
                    }}
                  />

                  <CustomSelect
                    label={{ name: "Currency" }}
                    name="currency"
                    options={[
                      { value: "INR", label: "INR" },
                      { value: "USD", label: "USD" },
                    ]}
                    value={values.currency}
                    onChange={(opt) => setFieldValue("currency", opt ? opt.value : "")}
                    className="g-col-6"
                  />
                  <CustomSelect
                    label={{ name: "Interval" }}
                    name="interval"
                    options={[
                      { value: "daily", label: "Daily" },
                      { value: "weekly", label: "Weekly" },
                      { value: "monthly", label: "Monthly" },
                      { value: "yearly", label: "Yearly" },
                      { value: "lifetime", label: "Lifetime" },
                    ]}
                    value={values.interval}
                    onChange={(opt) => setFieldValue("interval", opt ? opt.value : "")}
                    className="g-col-6"
                  />
                  <InputField
                    className="g-col-6"
                    label={{ name: "Interval Count" }}
                    input={{
                      id: "interval_count",
                      name: "interval_count",
                      type: "number",
                      min: 0,
                      placeholder: "Enter interval count",
                      value: values.interval_count,
                      onChange: handleChange,
                    }}
                  />
                  <Form.Group className="g-col-6">
                    <FormLabel labelName="Trial Available?" />
                    <Form.Check
                      type="switch"
                      checked={values.is_trial_available}
                      onChange={(e) => setFieldValue("is_trial_available", e.target.checked)}
                      className="mt-2 shadow-none custom-plan-switch"
                    />
                  </Form.Group>
                  {values.is_trial_available && (
                    <InputField
                      className="g-col-6"
                      label={{ name: "Trial Days" }}
                      input={{
                        id: "trial_days",
                        name: "trial_days",
                        type: "number",
                        min: 0,
                        placeholder: "Enter price",
                        value: values.trial_days,
                        onChange: handleChange,
                      }}
                      validation={{
                        isInvalid: touched.trial_days && errors.trial_days,
                        message: errors.trial_days,
                      }}
                    />)}

                  <InputField
                    className="g-col-12 mb-2"
                    label={{ name: "Description" }}
                    input={{
                      id: "description",
                      as: "textarea",
                      rows: 3,
                      name: "description",
                      placeholder: "Describe the plan's purpose",
                      value: values.description,
                      onChange: handleChange,
                    }}
                  />

                  <InputField
                    className="g-col-12 mb-2"
                    label={{ name: "Notes" }}
                    input={{
                      id: "notes",
                      as: "textarea",
                      rows: 3,
                      name: "notes",
                      placeholder: "Enter internal notes",
                      value: values.notes,
                      onChange: handleChange,
                    }}
                  />
                  {/* Features FieldArray Section */}
                  <Form.Group className="g-col-12 mb-2">
                    <div className="d-flex align-items-center justify-content-between mb-3 border-bottom pb-2">
                      <h5 className="mb-0">Plan Features</h5>
                      <CommonButton
                        title="Add Feature"
                        variant="outline-primary"
                        size="sm"
                        icon={<PlusIcon width={16} height={16} />}
                        onClick={() => {
                          const available = features.filter(
                            (f) => !values.features.some((vf) => vf.feature_id === f.id)
                          );
                          if (available.length > 0) {
                            const first = available[0];
                            let initialValue = "";
                            if (first.value_type === "boolean") initialValue = true;
                            if (first.value_type === "number") initialValue = 0;

                            setFieldValue("features", [
                              ...values.features,
                              { feature_id: first.id, value: initialValue },
                            ]);
                          } else {
                            // here set warning
                            toast.error("All available features already added");
                          }
                        }}
                      />
                    </div>

                    <FieldArray name="features">
                      {({ remove }) => (
                        <div className="d-flex flex-column gap-3">
                          {values.features.length === 0 ? (
                            <div className="text-center py-3 text-muted border rounded-2 bg-light fst-italic">
                              No features added. Click "Add Feature" to link subscription perks.
                            </div>
                          ) : (
                            values.features.map((feature, index) => {
                              const featureDetail = features.find(
                                (f) => f.id === feature.feature_id
                              );

                              return (
                                <div key={index} className="grid align-items-center">
                                  <CustomSelect
                                    label={{ name: index === 0 ? "Select Feature" : "", isHidden: index > 0 }}
                                    options={features
                                      .filter(
                                        (f) =>
                                          f.id === feature.feature_id ||
                                          !values.features.some((vf) => vf.feature_id === f.id)
                                      )
                                      .map((f) => ({
                                        value: f.id,
                                        label: `${f.name} (${f.code})`,
                                      }))}
                                    value={feature.feature_id}
                                    onChange={(opt) => {
                                      if (!opt) {
                                        setFieldValue(`features.${index}.feature_id`, "");
                                        setFieldValue(`features.${index}.value`, "");
                                        return;
                                      }
                                      const newDetail = features.find((f) => f.id === opt.value);
                                      let newValue = "";
                                      if (newDetail) {
                                        if (newDetail.value_type === "boolean") newValue = true;
                                        if (newDetail.value_type === "number") newValue = 0;
                                      }

                                      setFieldValue(`features.${index}.feature_id`, opt.value);
                                      setFieldValue(`features.${index}.value`, newValue);
                                    }}
                                    className="g-col-5"
                                  />
                                  <Form.Group className="g-col-5">
                                    {index === 0 && <FormLabel labelName="Value" />}
                                    {renderFeatureValueInput(
                                      featureDetail?.value_type,
                                      feature.value,
                                      (val) => setFieldValue(`features.${index}.value`, val)
                                    )}
                                  </Form.Group>

                                  <div className="g-col-2 text-end" >
                                    <button
                                      className="cursor-pointer border-0 bg-light p-2 rounded-circle d-inline-flex hover-shadow transition-all text-danger"
                                      style={{ marginTop: index === 0 ? "24px" : "0px" }}
                                      onClick={() => remove(index)}
                                    >
                                      <Cancel01Icon size={18} />
                                    </button>
                                  </div>
                                </div>
                              );
                            })
                          )}
                        </div>
                      )}
                    </FieldArray>
                  </Form.Group>

                </Card.Body>
                <Card.Footer className="bg-white text-end p-4">
                  <CommonButton
                    title="Cancel"
                    variant="outline"
                    className="me-3"
                    onClick={() => navigate("/subscriptions/plans")}
                  />
                  <CommonButton
                    title={isEditMode ? "Update" : "Create"}
                    variant="primary"
                    type="submit"
                    disabled={isSubmitting}
                  />
                </Card.Footer>
              </Form>
            )}
          </Formik>
        )}

      </Card>
    </Container>
  );
};

export default CreateEditPlan;
